import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ASSIGN_VALUE } from '@/utils/constants';
import { VALIDATION_STATE_WARNING } from '@/utils/validationErrors';

interface GtinInputProps {
  value: string | undefined | null;
  onChange: (value: string) => void;
  onInputFocusChange?: (hasFocus: boolean) => void;
  hasError?: boolean;
  validationState?: string;
  disabled?: boolean;
  modeSelectorDisabled?: boolean;
  hideModeSelector?: boolean;
}

export default function GtinInput({
  value,
  onChange,
  onInputFocusChange,
  hasError,
  validationState,
  disabled,
  modeSelectorDisabled,
  hideModeSelector,
}: GtinInputProps) {
  const { t } = useTranslation();
  // Determine if the initial value implies "Assign" mode (ASSIGN_VALUE only)
  const isAssignMode = value === ASSIGN_VALUE;

  const [mode, setMode] = useState<'assign' | 'enter'>(
    isAssignMode ? 'assign' : 'enter'
  );
  const [upcValue, setUpcValue] = useState<string>(
    isAssignMode ? '' : value || ''
  );

  // Sync state if the value prop changes externally (e.g., when a new album loads)
  useEffect(() => {
    const currentIsAssign = value === ASSIGN_VALUE;
    setMode(currentIsAssign ? 'assign' : 'enter');
    if (!currentIsAssign) {
      setUpcValue(value || '');
    }
  }, [value]);

  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMode = e.target.value as 'assign' | 'enter';
    setMode(newMode);

    if (newMode === 'assign') {
      onChange(ASSIGN_VALUE);
    } else {
      // If changing to 'enter', use the existing upcValue (removing the ASSIGN_VALUE word)
      const newValue = upcValue === ASSIGN_VALUE ? '' : upcValue;
      setUpcValue(newValue);
      onChange(newValue);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setUpcValue(newValue);
    onChange(newValue);
  };

  const validationClassName =
    validationState === VALIDATION_STATE_WARNING
      ? 'warning-input'
      : 'is-invalid invalid-input';

  return (
    <div className="identifier-input-container">
      {!hideModeSelector && (
        <select
          value={mode}
          onChange={handleDropdownChange}
          className={`form-select identifier-input-select ${hasError ? validationClassName : ''}`}
          disabled={disabled || modeSelectorDisabled}
        >
          <option value="enter">{t('product.editProduct.enterGtin')}</option>
          <option value="assign">{t('product.editProduct.assignGtin')}</option>
        </select>
      )}

      {(hideModeSelector || mode === 'enter') && (
        <input
          id="gtin"
          type="text"
          value={hideModeSelector ? value || '' : upcValue}
          onChange={handleInputChange}
          onFocus={() => onInputFocusChange?.(true)}
          onBlur={() => onInputFocusChange?.(false)}
          className={`form-control ${hasError && !disabled ? validationClassName : ''}`}
          placeholder={t('product.editProduct.enterGtin')}
          disabled={disabled}
        />
      )}
    </div>
  );
}
